// Scheduled tile visibility.
// Editors can attach a "Timing" piece (component id "timing") to a tile in Crystallize with two
// datetime fields, "start-date" and "end-date". The tile is only rendered while the current time
// is inside the window. Open-ended ranges are allowed: only a start makes the tile visible from the
// start onward, only an end keeps it visible until the end, and no piece (or no dates) is
// always-visible.
//
// This module is isomorphic (used server-side when fetching page tiles and client-side in the tile
// renderers), so it mirrors tile-country-visibility.ts: a minimal structural interface decoupled
// from the generated GraphQL types, and a pure predicate that takes `now` as a parameter so both
// call sites stay explicit and tests stay deterministic.

import { parseISO } from 'date-fns';

export const TIMING_COMPONENT_ID = 'timing';
export const TIMING_START_DATE_ID = 'start-date';
export const TIMING_END_DATE_ID = 'end-date';

interface TimingDatetimeContent {
  __typename?: string;
  datetime?: string | null;
}

interface TimingPieceChildComponent {
  id?: string | null;
  content?: TimingDatetimeContent | null;
}

interface TimingPieceContent {
  __typename?: string;
  components?: Array<TimingPieceChildComponent | null> | null;
}

interface TimingTileComponent {
  id?: string | null;
  content?: TimingPieceContent | null;
}

export interface TimingFilterableTile {
  components?: Array<TimingTileComponent | null> | null;
}

// Reads the datetime of a piece child component by id and parses it. An unset or invalid datetime
// is treated as "not set" (null) so an editor mistake never silently hides content.
function getBoundaryDate(
  components: Array<TimingPieceChildComponent | null>,
  childId: string,
): Date | null {
  const child = components.find((component) => component?.id === childId);
  const datetime = child?.content?.datetime;

  if (!datetime) {
    return null;
  }

  const parsed = parseISO(datetime);

  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function isTileVisibleAtTime(tile: TimingFilterableTile, now: Date): boolean {
  const timingComponent = (tile.components ?? []).find(
    (component) =>
      component?.id === TIMING_COMPONENT_ID && component.content?.__typename === 'PieceContent',
  );

  const pieceComponents = timingComponent?.content?.components;

  // No Timing piece (or an empty one) means the tile is always visible.
  if (!pieceComponents || pieceComponents.length === 0) {
    return true;
  }

  const start = getBoundaryDate(pieceComponents, TIMING_START_DATE_ID);
  const end = getBoundaryDate(pieceComponents, TIMING_END_DATE_ID);

  // Boundaries are inclusive; an unset boundary leaves that side of the window open.
  return (!start || now >= start) && (!end || now <= end);
}

export function filterTilesByTiming<T extends TimingFilterableTile>(tiles: T[], now: Date): T[] {
  return tiles.filter((tile) => isTileVisibleAtTime(tile, now));
}
